import { IdsInputInterface } from '../../components/ids-input/ids-input-attributes';
import { attributes } from '../../core/ids-attributes';
import { IdsBaseConstructor } from '../../core/ids-element';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import type { IdsValidationErrorMessage } from './ids-validation-mixin';

/**
 * Adds validation to any input field
 * @param {any} superclass Accepts a superclass and creates a new subclass from it
 * @returns {any} The extended object
 */
const IdsValidationInputMixin = <T extends IdsBaseConstructor>(superclass: T) => class extends superclass {
  constructor(...args: any[]) {
    super(...args);
  }

  static get attributes() {
    return [
      ...(superclass as any).attributes,
      attributes.SUPPRESS_VALIDATION,
      attributes.SUPPRESS_ERROR_MESSAGE
    ];
  }

  connectedCallback() {
    super.connectedCallback?.();
  }

  hideErrorMessage(toHide: boolean): void {
    (this as IdsInputInterface).input.hideErrorMessage(toHide);
  }

  /**
   * Sets whether validation is suppressed on the inner input element.
   * @param {boolean | string} value true to suppress validation
   */
  set suppressValidation(value: boolean | string) {
    const val = stringToBool(value);
    this.toggleAttribute(attributes.SUPPRESS_VALIDATION, val);
    if (val) {
      this.removeAllValidationMessages();
    }
    this.relayAttrValueChangeToInput(attributes.SUPPRESS_VALIDATION, val);
  }

  /**
   * Gets whether validation is suppressed on the inner input element.
   * @returns {boolean} true if validation is suppressed
   */
  get suppressValidation(): boolean {
    return this.hasAttribute(attributes.SUPPRESS_VALIDATION);
  }

  /**
   * Sets whether validation is suppressed on the inner input element.
   * @param {boolean | string} value true to suppress validation
   */
  set suppressErrorMessage(value: boolean | string) {
    const val = stringToBool(value);
    this.toggleAttribute(attributes.SUPPRESS_ERROR_MESSAGE, val);
    this.relayAttrValueChangeToInput(attributes.SUPPRESS_ERROR_MESSAGE, val);
  }

  /**
   * Gets whether validation is suppressed on the inner input element.
   * @returns {boolean} true if validation is suppressed
   */
  get suppressErrorMessage(): boolean {
    return this.hasAttribute(attributes.SUPPRESS_ERROR_MESSAGE);
  }

  /**
   * Return if the field is valid or not
   * @returns {boolean} true if invalid
   */
  get isValid(): boolean { return (this as IdsInputInterface).input?.shadowRoot?.querySelectorAll('.validation-message').length === 0; }

  /**
   * Return if the current validation errors
   * @returns {Array<IdsValidationErrorMessage>} The current errors
   */
  get validationMessages(): Array<IdsValidationErrorMessage> {
    const msgs: Array<IdsValidationErrorMessage> = [];
    (this as IdsInputInterface).input?.shadowRoot?.querySelectorAll('.validation-message').forEach((message: Element) => {
      msgs.push({
        message: message.querySelector('ids-text')?.childNodes[1].textContent || '',
        type: message.getAttribute('type') || '',
        id: message.getAttribute('validation-id') || ''
      });
    });
    return msgs;
  }

  get validationMessageElems(): Array<HTMLElement> {
    return [...(this as IdsInputInterface).input?.shadowRoot?.querySelectorAll('.validation-message') || []];
  }

  /**
   * Relays error message to IdsInput's addValidationMessage()
   * @param {Array<IdsValidationErrorMessage> | IdsValidationErrorMessage} message error message
   */
  addValidationMessage(message: Array<IdsValidationErrorMessage> | IdsValidationErrorMessage) {
    (this as IdsInputInterface).input?.addValidationMessage(message);
  }

  /**
   * Relays error message to IdsInput's removeValidationMessage()
   * @param {Array<IdsValidationErrorMessage> | IdsValidationErrorMessage} message error message
   */
  removeValidationMessage(message: Array<IdsValidationErrorMessage> | IdsValidationErrorMessage): void {
    (this as IdsInputInterface).input?.removeValidationMessage(message);
  }

  /**
   * Remove all validation messages from the inner input element
   * @returns {void}
   */
  removeAllValidationMessages(): void {
    (this as IdsInputInterface).input?.removeAllValidationMessages();
  }

  /**
   * Relays an attribute value change to the underlying input/trigger-field element.
   * @param {string} attribute the attribute name to set
   * @param {string} value the attribute value to set
   */
  relayAttrValueChangeToInput(attribute: string, value: boolean | string): void {
    const input = (this as IdsInputInterface).input;
    if (!input) {
      return;
    }

    const val = stringToBool(value);
    if (!val) {
      input.removeAttribute(attribute);
      return;
    }

    input.setAttribute(attribute, value);
  }
};

export default IdsValidationInputMixin;
